import React, { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { ModalProps } from '../types'
import { useMediaQuery } from '../hooks/useMediaQuery'

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  disableClickOutside = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const isMobile = useMediaQuery('(max-width: 768px)')

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!disableClickOutside && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose, disableClickOutside])

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const sizeClasses = {
    sm: isMobile ? 'w-full h-full max-w-none' : 'max-w-md',
    md: isMobile ? 'w-full h-full max-w-none' : 'max-w-lg',
    lg: isMobile ? 'w-full h-full max-w-none' : 'max-w-2xl',
    xl: isMobile ? 'w-full h-full max-w-none' : 'max-w-4xl',
    '2xl': isMobile ? 'w-full h-full max-w-none' : 'max-w-6xl'
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
      aria-describedby="modal-content"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        aria-hidden="true"
      />

      <div className={`flex ${isMobile ? 'min-h-full items-end' : 'min-h-full items-center'} justify-center ${isMobile ? 'p-0' : 'p-4'}`}>
        <div
          ref={modalRef}
          className={`relative w-full ${sizeClasses[size]} transform overflow-hidden ${isMobile ? 'rounded-t-2xl' : 'rounded-lg'} bg-white shadow-xl transition-all ${isMobile ? 'animate-slide-up' : 'animate-fade-in'}`}
          role="document"
          tabIndex={-1}
        >
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 sticky top-0 bg-white z-10">
              <h3
                id="modal-title"
                className="text-base sm:text-lg font-semibold text-gray-900"
              >
                {title}
              </h3>
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="rounded-md p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Fechar modal"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          <div
            id="modal-content"
            className="px-4 py-4 sm:px-6 sm:py-4 pb-20 lg:pb-4 overflow-y-auto max-h-[calc(100vh-160px)]"
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'info'
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  type = 'danger'
}) => {
  const typeStyles = {
    danger: {
      button: 'bg-danger-600 hover:bg-danger-700 focus:ring-danger-500',
      icon: '⚠️'
    },
    warning: {
      button: 'bg-warning-600 hover:bg-warning-700 focus:ring-warning-500',
      icon: '⚠️'
    },
    info: {
      button: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
      icon: 'ℹ️'
    }
  }

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="text-2xl">{typeStyles[type].icon}</div>
          <p className="text-gray-600 text-sm sm:text-base">{message}</p>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end space-y-reverse space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
          <button
            onClick={onClose}
            className="w-full sm:w-auto min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            aria-label={`${cancelText} e fechar modal`}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`w-full sm:w-auto min-h-[44px] px-4 py-2 text-sm font-medium text-white ${typeStyles[type].button} border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors`}
            aria-label={`${confirmText} ação`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  )
}

interface LoadingModalProps {
  isOpen: boolean
  message?: string
}

export const LoadingModal: React.FC<LoadingModalProps> = ({
  isOpen,
  message = 'A carregar...'
}) => {
  return (
    <Modal isOpen={isOpen} onClose={() => {}} showCloseButton={false} size="sm">
      <div
        className="flex items-center justify-center space-x-3 py-8"
        role="status"
        aria-live="polite"
      >
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
          aria-hidden="true"
        ></div>
        <span className="text-gray-600" aria-label={`A carregar: ${message}`}>
          {message}
        </span>
      </div>
    </Modal>
  )
}

export default Modal
